import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

/** Keys of the preferences in the browser storage */
const PINNED_MENU_KEY = "dp.menuPinned";
const SERVICES_OPEN_KEY = "dp.menuServicesOpen";

/**
 * Preference of the user for the side menu: pinned (always open on the left on wide screens) or
 * opened with the menu button. Kept in the browser, per device.
 */
@Injectable({
  providedIn: "root",
})
export class MenuPreferenceService {
  private _isPinned = new BehaviorSubject<boolean>(this.readFlag(PINNED_MENU_KEY, false));
  /** Group of the services open: by default on wide screens, closed on phones (shorter menu) */
  private _servicesOpen: boolean = this.readFlag(SERVICES_OPEN_KEY, window.innerWidth >= 992);

  /** The menu is pinned (it is shown only on wide screens anyway) */
  public get isPinned$(): Observable<boolean> {
    return this._isPinned.asObservable();
  }

  public get isPinned(): boolean {
    return this._isPinned.value;
  }

  /** Pin or unpin the menu and remember the choice */
  public setPinned(pinned: boolean): void {
    this._isPinned.next(pinned);
    this.writeFlag(PINNED_MENU_KEY, pinned);
  }

  /** The group of the services is open in the menu */
  public get isServicesOpen(): boolean {
    return this._servicesOpen;
  }

  /** Open or close the group of the services and remember the choice */
  public setServicesOpen(open: boolean): void {
    this._servicesOpen = open;
    this.writeFlag(SERVICES_OPEN_KEY, open);
  }

  /** Saved choice, or the default when there is none (or the storage is not available) */
  private readFlag(key: string, defaultValue: boolean): boolean {
    try {
      const value = localStorage.getItem(key);
      return value === null ? defaultValue : value === "1";
    } catch {
      return defaultValue;
    }
  }

  private writeFlag(key: string, value: boolean): void {
    try {
      localStorage.setItem(key, value ? "1" : "0");
    } catch {
      // Storage not available (private mode...): the choice lasts until the page is reloaded
    }
  }
}
